import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { RootState } from '@/store/store';
import styled from 'styled-components';
import { TextField, Button } from '@/components/atoms';
import { filterValidQueryParams } from '@/utils/Filters';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { IRangeSelectorCmp } from './IRangeSelectorCmp';
import Image from 'next/image';

interface IStyledProps {
  $active?: boolean;
  $showBackgroundColors?: boolean;
  value?: string;
}

const RangeWrapper = styled.div<IStyledProps>`
  display: flex;
  justify-content: space-between;
  width: 80%;
`;

const RangeGrid = styled.div<IStyledProps>`
  display: grid;
  grid-template-columns: 70px 70px 40px;
  grid-template-rows: 24px auto;
  align-items: center;
  column-gap: 8px;
  row-gap: 6px;
`;

const InputTitle = styled.div<IStyledProps>`
  height: 16px;
  font-size: 14px;
  color: ${props => props.theme.paletteDefault.primary.main};
`;

const CurrencyWrapper = styled.div<IStyledProps>`
  color: ${props => props.theme.paletteDefault.primary.main};
`;

const CustomButton = styled(Button)`
  font-size: 10px;
  grid-column: 1 / 3;
  height: 10px;
`;
const CustomInput = styled(TextField)<IStyledProps>`
  .MuiOutlinedInput-notchedOutline {
    border-color: ${props =>
      props.value
        ? props.theme.paletteDefault.primary.main
        : props.theme.paletteDefault.primary.main};
    border-width: '2px';
  }

  .MuiInputBase-input:not(:focus) {
    background-color: ${props =>
      props.value && props.$showBackgroundColors
        ? props.theme.paletteDefault.grey1
        : 'transparent'};
  }

  input {
    ::-webkit-outer-spin-button,
    ::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    -moz-appearance: textfield;
    text-align: center;
    padding: 8px 4px;
    font-size: 14px;
  }
`;

const CloseIconWrapper = styled.div`
  color: ${props => props.theme.paletteDefault.grey1};
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const initRange = (active: boolean, queryParam: string) => {
  if (active && queryParam) {
    const tokens = queryParam.split('-');
    if (tokens && tokens.length > 0) {
      return {
        from: tokens[0],
        to: tokens[1] || '',
      };
    }
  }

  return { from: '', to: '' };
};

const RangeSelector: React.FC<IRangeSelectorCmp> = ({
  active,
  applyHandler,
  showButton = true,
  showBackgroundColors = false,
  inputRange,
  resetHandler,
}) => {
  const { t } = useTranslation(['common']);
  const currency = useSelector(
    (state: RootState) => state.currencyReducer.currency
  );
  const router = useRouter();
  const { slug, ...queryParameters } = router.query;
  const validQueryParams = filterValidQueryParams(queryParameters);

  const initialRange = initRange(
    active,
    inputRange || (validQueryParams?.price as string)
  );

  const [range, setRange] = useState(initialRange);
  const [error, setError] = useState<{ from: boolean; to: boolean }>({
    from: false,
    to: false,
  });

  useEffect(() => {
    const range = initRange(
      active,
      inputRange || (validQueryParams?.price as string)
    );
    setRange(range);
  }, [router.asPath, inputRange]);

  const submitHandler = async (e?: React.SyntheticEvent) => {
    if (e) {
      e.preventDefault();
    }
    setError({ from: false, to: false });
    let errObj = {};

    if (!range || (range.from === '' && range.to === '')) {
      return;
    }
    let fromNum = Math.round(Number(range?.from));

    let toNum = Math.round(Number(range?.to));

    if (range.from && (isNaN(fromNum) || fromNum < 0)) {
      (errObj as any).from = true;
    }
    if (range.to && (isNaN(toNum) || toNum < 0)) {
      (errObj as any).to = true;
    }

    if (Object.keys(errObj).length === 0) {
      if (
        range.from ||
        range.to ||
        (range.from && range.to && fromNum < toNum)
      ) {
        let priceValue = null;
        if (range.from && range.to) {
          if (fromNum < toNum) {
            priceValue = `${fromNum}-${toNum}`;
          } else {
            (errObj as any).to = true;
          }
        } else {
          if (range.to) {
            priceValue = `0-${toNum}`;
          } else {
            priceValue = `${fromNum}`;
          }
        }

        if (priceValue) {
          applyHandler(priceValue, router);
        }
      }
    }

    setError(
      Object.keys(errObj).length === 0
        ? { from: false, to: false }
        : { ...error, ...errObj }
    );
  };

  const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError({
      ...error,
      [e.target.name as 'from' | 'to']: false,
    });

    setRange({
      ...range,
      [e.target.name]: e.target.value,
    });
  };

  const clickAwayHandler = () => {
    setError({ from: false, to: false });
  };

  const focusHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    setError({ ...error, [e.target.name]: false });
  };

  useEffect(() => {
    if (!showButton) {
      submitHandler();
    }
  }, [range]);

  return (
    <ClickAwayListener onClickAway={clickAwayHandler}>
      <form onSubmit={submitHandler}>
        <RangeWrapper>
          <RangeGrid $active={active}>
            <InputTitle $active={active || range?.from ? true : false}>
              {t('common:from')}
            </InputTitle>
            <InputTitle $active={active || range?.to ? true : false}>
              {t('common:to')}
            </InputTitle>
            <InputTitle></InputTitle>
            <CustomInput
              name="from"
              variant="outlined"
              $active={active || range?.from ? true : false}
              size="small"
              value={range?.from}
              onChange={changeHandler}
              onFocus={focusHandler}
              error={error ? error['from'] : false}
              $showBackgroundColors={showBackgroundColors}
            />
            <CustomInput
              name="to"
              variant="outlined"
              $active={active || range?.to ? true : false}
              size="small"
              value={range?.to}
              onChange={changeHandler}
              onFocus={focusHandler}
              error={error ? error['to'] : false}
              $showBackgroundColors={showBackgroundColors}
            />
            <CurrencyWrapper
              $active={active && (range?.from || range?.to) ? true : false}>
              {currency && currency.symbol}
            </CurrencyWrapper>
            {showButton && (
              <CustomButton type="submit">{t('common:apply')}</CustomButton>
            )}
          </RangeGrid>
          {resetHandler && (
            <CloseIconWrapper
              onClick={() => {
                setRange({ from: '', to: '' });
                resetHandler();
              }}>
              <Image
                src="/asset/closeIcon.svg"
                alt={'close icon'}
                width={18}
                height={18}
                unoptimized={true}
              />
            </CloseIconWrapper>
          )}
        </RangeWrapper>
      </form>
    </ClickAwayListener>
  );
};

export default RangeSelector;
